import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Trading.css';
import { authenticatedFetch, getToken, logout, fetchUserInfo as fetchUserInfoFromAuth, clearUserInfoCache, isRateLimited, getRateLimitRemaining } from '../utils/auth';
import Login from '../components/Login';
import Profile from '../components/Profile';
import TradingHeader from '../components/TradingHeader';
import IntroSection from '../components/Trading/IntroSection';
import Footer from '../components/Footer';
import NotificationSettingsTab from '../components/Trading/tabs/NotificationSettingsTab';
import HistoryTab from '../components/Trading/tabs/HistoryTab';
import TradeTab from '../components/Trading/tabs/TradeTab';
import PortfolioTab from '../components/Trading/tabs/PortfolioTab';
import BalanceTab from '../components/Trading/tabs/BalanceTab';
import AITradeTab from '../components/Trading/tabs/AITradeTab';
import { debounce, formatChange, TRADING_TABS } from '../components/Trading/utils';
import { useOrderHistory, useTradingAuth } from '../hooks/useOrderHistory';
import AutoTradeModal from '../components/Trading/modals/AutoTradeModal';
import ScheduledOrderModal from '../components/Trading/modals/ScheduledOrderModal';
import AITradeConfigModal from '../components/Trading/modals/AITradeConfigModal';

// 가격 변동 포맷 함수는 utils.js에서 import하므로 제거됨

const Trading = () => {
  const {
    user,
    setUser,
    authError,
    setAuthError,
    showLogin,
    setShowLogin,
    loadUser,
  } = useTradingAuth();

  const {
    orderHistory,
    orderHistoryLoading,
    orderHistoryRateLimited,
    orderHistoryRateLimitUntil,
    dateFilter,
    setDateFilter,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    fetchOrderHistory,
  } = useOrderHistory(user);

  // 기본 상태
  const [selectedStock, setSelectedStock] = useState('');
  const [stockData, setStockData] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceError, setBalanceError] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [performanceVisible, setPerformanceVisible] = useState(false);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);
  const [performanceRange, setPerformanceRange] = useState({
    type: 'week',
    startDate: '',
    endDate: ''
  });
  const [orderType, setOrderType] = useState('buy');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [orderMethod, setOrderMethod] = useState('limit'); // limit, market
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingName, setRefreshingName] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState(null);
  const [aiQuestionLoading, setAiQuestionLoading] = useState(false);
  const [autoTrades, setAutoTrades] = useState([]);
  const [showAutoTradeModal, setShowAutoTradeModal] = useState(false);
  const [selectedAutoTrade, setSelectedAutoTrade] = useState(null);
  const [scheduledOrders, setScheduledOrders] = useState([]);
  const [showScheduledOrderModal, setShowScheduledOrderModal] = useState(false);
  const [selectedScheduledOrder, setSelectedScheduledOrder] = useState(null);
  const [showFavoriteStockModal, setShowFavoriteStockModal] = useState(false);
  
  // API 호출 제한 방지를 위한 상태 관리
  const [isFetching, setIsFetching] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState({});
  const [simpleExplanation, setSimpleExplanation] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [itemExplanations, setItemExplanations] = useState({}); // 항목별 해석 저장
  const [itemExplanationLoading, setItemExplanationLoading] = useState({});
  const [showProfile, setShowProfile] = useState(false);

  // 분석 도구/범위 표기용 메타데이터 생성 (주문 메뉴 개별 표기)
  const buildAnalysisMetadata = (analysis, symbol = '', stockName = '') => {
    if (!analysis) return { tools: ['정보 없음'], scope: { 분석유형: '단일 종목' }, confidence: 0 };
    const type = analysis.analysis_type || 'combined';
    const hasGoogle = !!(analysis.google_ai_analysis && Object.keys(analysis.google_ai_analysis).length);
    const hasLocal = !!(analysis.local_ai_analysis && Object.keys(analysis.local_ai_analysis).length);
    let tools = [];
    if (type === 'basic') tools = ['규칙 기반'];
    else {
      if (type === 'google_ai' || hasGoogle) tools.push('Google AI (Gemini)');
      if (type === 'local_ai' || hasLocal) tools.push('로컬 AI (Ollama)');
      if (tools.length === 0) tools.push('규칙 기반');
    }
    const sym = (analysis.symbol || symbol || '').toString().trim();
    const name = (analysis.stock_name || stockName || '').toString().trim();
    const scope = {
      종목코드: sym || '-',
      종목명: name || '-',
      상태: analysis.current_status || '관찰중',
      분석유형: '단일 종목'
    };
    const rec = analysis.recommendation || {};
    const confidence = rec.confidence ?? rec.confidence_score ?? 0;
    return { tools, scope, confidence };
  };
  const [activeTab, setActiveTab] = useState(() => (getToken() ? 'trade' : 'intro'));
  const [aiTradeConfig, setAiTradeConfig] = useState(null);
  const [aiTradeStatus, setAiTradeStatus] = useState({ is_active: false, has_api_key: false });
  const [showHeader, setShowHeader] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('trading_header_visible');
      if (stored !== null) setShowHeader(stored === 'true');
    } catch (e) {}
  }, []);

  const toggleHeader = () => {
    setShowHeader(prev => {
      const next = !prev;
      try {
        localStorage.setItem('trading_header_visible', String(next));
      } catch (e) {}
      return next;
    });
  };

  // AI 분석 리스트 조회 중복 호출 방지
  const fetchAiAnalysesInFlightRef = useRef(null);
  const aiConfigFetchRef = useRef({ isFetching: false, lastFetchedAt: 0 });
  const fetchAiTradeConfig = useCallback(async (force = false) => {
    if (aiConfigFetchRef.current.isFetching) return;
    const now = Date.now();
    if (!force && now - aiConfigFetchRef.current.lastFetchedAt < 5000) return;
    aiConfigFetchRef.current.isFetching = true;
    try {
      const response = await authenticatedFetch('/api/ai-trade/config', { timeout: 120000 });
      if (response.ok) {
        const data = await response.json();
        setAiTradeConfig({
          ...data.config,
          using_env_key: data.using_env_key ?? data.config?.using_env_key ?? false,
        });
        aiConfigFetchRef.current.lastFetchedAt = Date.now();
      }
    } catch (error) {
      console.error('AI 설정 조회 오류:', error);
    } finally {
      aiConfigFetchRef.current.isFetching = false;
    }
  }, []);
  const [dbConnectionStatus, setDbConnectionStatus] = useState({ status: 'checking', message: '' });

  // trading-service 상태 체크 (health) 및 시장 상태
  const [tradingServiceStatus, setTradingServiceStatus] = useState({ status: 'unknown', info: null, lastChecked: null });
  const [serviceEnabled, setServiceEnabled] = useState(true);
  const [serviceToggleLoading, setServiceToggleLoading] = useState(false);
  const [marketStatus, setMarketStatus] = useState('불명'); // '주문가능시간' | '정규시장종료'

  const [showAiTradeModal, setShowAiTradeModal] = useState(false);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [useGemini3Flash, setUseGemini3Flash] = useState(false); // Gemini 3 Flash 사용 여부 (기본값: false, Gemini 2.5 Flash 사용)
  const [showAnalysisDetail, setShowAnalysisDetail] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [showWatchlist, setShowWatchlist] = useState(false);
  // const [showAddWatchlistModal, setShowAddWatchlistModal] = useState(false); // 미사용 변수 제거
  const [popularStocksTab, setPopularStocksTab] = useState('popular'); // popular, ai-recommend, favorite
  const [popularStocksDropdownOpen, setPopularStocksDropdownOpen] = useState(false);
  const [portfolioDropdownOpen, setPortfolioDropdownOpen] = useState(false);
  const [popularStocksList, setPopularStocksList] = useState([]);
  const [aiRecommendedStocksList, setAiRecommendedStocksList] = useState([]);
  const [watchlistStocksList, setWatchlistStocksList] = useState([]);
  const [favoriteStocksList, setFavoriteStocksList] = useState([]); // 즐겨찾기 종목 리스트

  // 인기 주식 목록 (KOSPI/KOSDAQ)
  const popularStocks = [
    { symbol: '005930', name: '삼성전자', market: 'KOSPI' },
    { symbol: '000660', name: 'SK하이닉스', market: 'KOSPI' },
    { symbol: '035420', name: 'NAVER', market: 'KOSPI' },
    { symbol: '035720', name: '카카오', market: 'KOSPI' },
    { symbol: '051910', name: 'LG화학', market: 'KOSPI' },
    { symbol: '207940', name: '삼성바이오로직스', market: 'KOSPI' },
    { symbol: '006400', name: '삼성SDI', market: 'KOSPI' },
    { symbol: '005380', name: '현대차', market: 'KOSPI' },
  ];

  // 종목 검색 (디바운스 적용)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchStock = useCallback(
    debounce(async (keyword) => {
      if (!keyword || keyword.trim().length < 1) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      const trimmedKeyword = keyword.trim();
      
      // 종목코드가 6자리인 경우 바로 조회
      if (/^\d{6}$/.test(trimmedKeyword)) {
        setIsSearching(true);
        try {
          const response = await authenticatedFetch(`/api/trading/stock/${trimmedKeyword}`, {
            timeout: 30000 // 30초 타임아웃
          });
          if (response.ok) {
            const stockData = await response.json();
            const result = {
              symbol: stockData.symbol || trimmedKeyword,
              name: stockData.name || stockData.stock_name || '종목명 조회 중...',
              stock_name: stockData.stock_name || stockData.name || '종목명 조회 중...',
              market: stockData.market || 'KOSPI/KOSDAQ',
              price: stockData.price || 0
            };
            setSearchResults([result]);
            setShowSearchResults(true);
            // 종목명이 있으면 검색어에도 표시
            if (stockData.name || stockData.stock_name) {
              setSearchKeyword(`${trimmedKeyword} - ${stockData.name || stockData.stock_name}`);
            }
          } else {
            // 종목코드로 조회 실패 시 일반 검색 시도
            try {
              const searchResponse = await authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, {
                timeout: 30000
              });
              if (searchResponse.ok) {
                const data = await searchResponse.json();
                setSearchResults(data.results || []);
                setShowSearchResults(true);
              } else {
                console.warn('종목 검색 실패:', searchResponse.status);
                setSearchResults([]);
                setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
              }
            } catch (searchError) {
              console.error('종목 검색 실패:', searchError);
              setSearchResults([]);
              setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
            }
          }
        } catch (error) {
          console.error('종목코드 조회 실패:', error);
          // 종목코드 조회 실패 시 일반 검색 시도
          try {
            const searchResponse = await authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, {
              timeout: 30000
            });
            if (searchResponse.ok) {
              const data = await searchResponse.json();
              setSearchResults(data.results || []);
              setShowSearchResults(true);
            } else {
              console.warn('종목 검색 실패:', searchResponse.status);
              setSearchResults([]);
              setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
            }
          } catch (searchError) {
            console.error('종목 검색 실패:', searchError);
            setSearchResults([]);
            setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
          }
        } finally {
          setIsSearching(false);
        }
        return;
      }

      // 종목명으로 검색 (2자 이상)
      if (trimmedKeyword.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }

      setIsSearching(true);
      try {
        const response = await authenticatedFetch(`/api/trading/search?keyword=${encodeURIComponent(trimmedKeyword)}`, {
          timeout: 30000 // 30초 타임아웃
        });
        if (response.ok) {
          const data = await response.json();
          setSearchResults(data.results || []);
          setShowSearchResults(true);
        } else {
          console.warn('종목 검색 실패:', response.status);
          setSearchResults([]);
          setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
        }
      } catch (error) {
        console.error('종목 검색 실패:', error);
        setSearchResults([]);
        setShowSearchResults(true); // 검색 결과 없음 표시를 위해 true 유지
      } finally {
        setIsSearching(false);
      }
    }, 300),
    []
  );

  // 검색어 변경 핸들러
  useEffect(() => {
    if (searchKeyword) {
      searchStock(searchKeyword);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchKeyword, searchStock]);

  // 주식 데이터 조회
  const fetchStockData = async (symbol, options = {}) => {
    if (!symbol) return;
    const force = options.force === true;
    const url = `/api/trading/stock/${symbol}${force ? '?force=true' : ''}`;
    
    // 종목코드 유효성 검사 (6자리 숫자만 허용)
    // PORTFOLIO_SUMMARY 같은 특수 문자열 필터링
    if (!/^\d{6}$/.test(symbol)) {
      console.warn('⚠️ 유효하지 않은 종목코드:', symbol);
      return;
    }
    
    setLoading(true);
    try {
      const response = await authenticatedFetch(url, {
        timeout: 30000 // 30초 타임아웃
      });
      
      // 502, 503 에러 처리
      if (response.status === 502 || response.status === 503) {
        console.error('서버 연결 오류:', response.status);
        setLoading(false);
        return;
      }
      
      // 404 에러 처리 (종목 정보 없음)
      if (response.status === 404) {
        console.warn('종목 정보를 찾을 수 없습니다:', symbol);
        // stockData를 기본값으로 설정 (null이 아닌 객체)
        setStockData({
          symbol: symbol,
          name: '',
          stock_name: '',
          price: 0,
          change: 0,
          changeRate: 0,
          changePercent: 0,
          volume: 0,
          open: 0,
          high: 0,
          low: 0
        });
        setLoading(false);
        return;
      }
      
      if (response.ok) {
        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          
          // 디버깅: 원본 응답 데이터 확인
          console.log('📥 원본 응답 데이터:', {
            symbol: data.symbol,
            name: data.name,
            price: data.price,
            change: data.change,
            changeRate: data.changeRate,
            hasChange: data.change !== undefined && data.change !== null,
            hasChangeRate: data.changeRate !== undefined && data.changeRate !== null,
            rawData: data
          });
          
          // 변동률 보정 (백엔드에서 보정되지 않은 경우)
          let correctedData = { ...data };
          if (Math.abs(data.changeRate) > 100 && data.change !== undefined && data.price > 0) {
            console.warn('⚠️ 프론트엔드: 비정상 변동률 감지, 보정 시도:', {
              original: data.changeRate,
              change: data.change,
              price: data.price
            });
            
            // 소수점 누락 가능성 체크
            if (Math.abs(data.changeRate) < 100000) {
              const possibleCorrectRate = data.changeRate / 100;
              if (Math.abs(possibleCorrectRate) <= 100) {
                const prevClose = data.price - data.change;
                if (prevClose > 0) {
                  const expectedRate = (data.change / prevClose) * 100;
                  if (Math.abs(possibleCorrectRate - expectedRate) < 10) {
                    correctedData.changeRate = possibleCorrectRate;
                    console.log('✅ 프론트엔드: 변동률 보정 성공:', possibleCorrectRate);
                  }
                }
              }
            }
            
            // 보정 실패 시 change와 price로 직접 계산
            if (Math.abs(correctedData.changeRate) > 100 && data.change !== 0 && data.price > 0) {
              const prevClose = data.price - data.change;
              if (prevClose > 0) {
                correctedData.changeRate = (data.change / prevClose) * 100;
                console.log('✅ 프론트엔드: 변동률 직접 계산:', correctedData.changeRate);
              }
            }
          }
          
          // changeRate를 changePercent로 변환 (API 응답 형식 통일)
          if (correctedData.changeRate !== undefined && correctedData.changePercent === undefined) {
            correctedData.changePercent = correctedData.changeRate;
          }
          
          // 디버깅: 받은 데이터 확인
          console.log('📥 주식 데이터 수신:', {
            symbol: correctedData.symbol,
            name: correctedData.name,
            price: correctedData.price,
            change: correctedData.change,
            changeRate: correctedData.changeRate,
            changePercent: correctedData.changePercent,
            hasName: !!correctedData.name,
            isNameSameAsSymbol: correctedData.name === correctedData.symbol,
            hasPrice: correctedData.price > 0,
            hasOpen: correctedData.open !== undefined,
            hasHigh: correctedData.high !== undefined,
            hasLow: correctedData.low !== undefined
          });
          
          // 데이터 검증: 필수 필드 확인
          if (!correctedData.symbol) {
            console.error('⚠️ 종목코드가 없습니다:', correctedData);
            return;
          }
          
          // stockData 업데이트 (null이 아닌 객체로 설정)
          setStockData({
            ...correctedData,
            // 기본값 설정 (undefined/null 방지)
            price: correctedData.price || 0,
            change: correctedData.change || 0,
            changeRate: correctedData.changeRate || 0,
            changePercent: correctedData.changePercent || 0,
            volume: correctedData.volume || 0,
            open: correctedData.open || 0,
            high: correctedData.high || 0,
            low: correctedData.low || 0
          });
          
          // 종목명이 없거나 종목코드와 같으면 자동으로 종목명 갱신 시도
          if (!correctedData.name || correctedData.name === correctedData.symbol) {
            console.warn('⚠️ 종목명이 없거나 종목코드와 동일, 자동 갱신 시도:', {
              symbol: correctedData.symbol,
              name: correctedData.name
            });
            
            // 종목명 자동 갱신 (약간의 지연 후 실행하여 상태 업데이트 완료 대기)
            setTimeout(async () => {
              try {
                const refreshResponse = await authenticatedFetch(`/api/trading/stock/${correctedData.symbol}/refresh-name`, {
                  method: 'POST',
                  timeout: 30000
                });
                
                if (refreshResponse.ok) {
                  const refreshData = await refreshResponse.json();
                  if (refreshData.success && refreshData.name) {
                    console.log('✅ 종목명 자동 갱신 성공:', refreshData.name);
                    // 종목명 갱신 후 stockData 업데이트
                    setStockData(prev => ({
                      ...prev,
                      name: refreshData.name,
                      stock_name: refreshData.name
                    }));
                  }
                }
              } catch (refreshError) {
                console.warn('⚠️ 종목명 자동 갱신 실패:', refreshError);
                // 자동 갱신 실패는 무시 (사용자가 수동으로 갱신할 수 있음)
              }
            }, 500);
          }
          
          // 시장가 주문 시 현재가 자동 입력
          if (orderMethod === 'market' && data.price) {
            setPrice(data.price.toFixed(0));
          }
        } else {
          console.error('주식 데이터 조회 실패: JSON 형식이 아닌 응답');
        }
      } else {
        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        let errorData;
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        } else {
          errorData = { error: '서버 연결 오류가 발생했습니다.' };
        }
        console.error('주식 데이터 조회 실패:', errorData.error);
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('주식 데이터 조회 실패:', error);
      
      // 에러 발생 시에도 stockData를 기본값으로 설정 (null이 아닌 객체)
      setStockData(prev => ({
        symbol: prev?.symbol || symbol || '',
        name: prev?.name || '',
        stock_name: prev?.stock_name || '',
        price: prev?.price || 0,
        change: prev?.change || 0,
        changeRate: prev?.changeRate || 0,
        changePercent: prev?.changePercent || 0,
        volume: prev?.volume || 0,
        open: prev?.open || 0,
        high: prev?.high || 0,
        low: prev?.low || 0
      }));
      
      if (error.message === 'Authentication required') {
        setAuthError(true);
      } else if (error.message && error.message.includes('서버 연결 오류')) {
        console.error('서버 연결 오류:', error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // 종목명 갱신 요청
  const refreshStockName = async () => {
    if (!selectedStock) return;

    setRefreshingName(true);
    try {
      const response = await authenticatedFetch(`/api/trading/stock/${selectedStock}/refresh-name`, {
        method: 'POST',
        timeout: 30000 // 30초 타임아웃
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`종목명이 갱신되었습니다: ${data.name}`);
          // 주식 데이터 다시 조회
          await fetchStockData(selectedStock);
        } else {
          alert(`종목명을 찾을 수 없습니다: ${data.message || '알 수 없는 오류'}`);
        }
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`종목명 갱신 실패: ${errorData.error || '알 수 없는 오류'}`);
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('종목명 갱신 실패:', error);
      alert('종목명 갱신 중 오류가 발생했습니다.');
      if (error.message === 'Authentication required') {
        setAuthError(true);
      }
    } finally {
      setRefreshingName(false);
    }
  };

  // AI 분석 옵션 상태
  const [aiAnalysisOptions, setAiAnalysisOptions] = useState({
    useLocalAI: true, // 기본값: 로컬 AI 사용
    useGoogleAI: false, // 기본값: Google AI 미사용
    googleAIModel: 'gemini-2.5-flash' // 기본 모델: Gemini 2.5 Flash
  });

  // AI 분석 요청 (기존 분석 결과 확인 후 갱신 여부 물어보기)
  const requestAiAnalysis = async () => {
    if (!selectedStock || !stockData) return;

    // 기존 분석 결과 확인
    const existingAnalysis = aiAnalyses && aiAnalyses.find(a => a.symbol === selectedStock);
    
    // 기존 분석 결과가 있으면 먼저 표시하고 갱신 여부 물어보기
    if (existingAnalysis) {
      const analysisDate = existingAnalysis.createdAt 
        ? new Date(existingAnalysis.createdAt).toLocaleString('ko-KR')
        : existingAnalysis.updatedAt 
          ? new Date(existingAnalysis.updatedAt).toLocaleString('ko-KR')
          : '알 수 없음';
      
      const recommendation = existingAnalysis.recommendation || {};
      let reasoningText = '';
      if (existingAnalysis.detailed_analysis) {
        reasoningText = existingAnalysis.detailed_analysis;
      } else if (existingAnalysis.summary) {
        reasoningText = existingAnalysis.summary;
      } else if (recommendation.reasoning) {
        reasoningText = Array.isArray(recommendation.reasoning) 
          ? recommendation.reasoning.join('\n') 
          : recommendation.reasoning;
      }
      
      const recommendationWithReasoning = {
        ...recommendation,
        reasoning: reasoningText || recommendation.reasoning || ''
      };
      
      setAiAnalysis({
        recommendation: recommendationWithReasoning,
        analysis: reasoningText || existingAnalysis.summary || '',
        confidence: recommendation.confidence || recommendation.confidence_score || 0,
        analysis_type: existingAnalysis.analysis_type || 'combined',
        local_ai: existingAnalysis.local_ai_analysis,
        google_ai: existingAnalysis.google_ai_analysis,
        current_status: existingAnalysis.current_status,
        trade_config: existingAnalysis.trade_config,
        combined_analysis: existingAnalysis.combined_analysis,
        analysisDate: analysisDate,
        isExisting: true,
        analysisMetadata: buildAnalysisMetadata(existingAnalysis, selectedStock, stockData?.name || stockData?.stock_name)
      });
      
      console.log('📋 기존 분석 결과 표시:', {
        symbol: selectedStock,
        analysisDate: analysisDate,
        recommendation: recommendation.action || 'none',
        confidence: recommendation.confidence || recommendation.confidence_score || 0
      });
      
      // 갱신 여부 확인
      const confirmMessage = `기존 분석 결과가 있습니다.\n\n` +
        `📅 분석 일자: ${analysisDate}\n` +
        `📊 추천: ${recommendation.action === 'buy' ? '매수' : recommendation.action === 'sell' ? '매도' : recommendation.action === 'hold' ? '보유' : '분석 완료'}\n` +
        `🎯 신뢰도: ${recommendation.confidence || recommendation.confidence_score || 0}%\n\n` +
        `현재 설정된 옵션으로 분석을 갱신하시겠습니까?\n\n` +
        `옵션:\n` +
        `• 로컬 AI: ${aiAnalysisOptions.useLocalAI ? '사용' : '미사용'}\n` +
        `• Google AI: ${aiAnalysisOptions.useGoogleAI ? '사용' : '미사용'}` +
        (aiAnalysisOptions.useGoogleAI ? ` (모델: ${aiAnalysisOptions.googleAIModel})` : '');
      
      if (!window.confirm(confirmMessage)) {
        return; // 취소 - 기존 분석 결과만 표시
      }
    }

    // 분석 실행
    await executeAiAnalysis();
  };

  // AI 분석 업데이트 요청 (기존 분석 결과 확인 후 갱신)
  const updateAiAnalysis = async () => {
    if (!selectedStock || !stockData) return;

    // 옵션 확인 모달
    const confirmMessage = `AI 분석 옵션:\n\n` +
      `• 로컬 AI: ${aiAnalysisOptions.useLocalAI ? '사용' : '미사용'}\n` +
      `• Google AI: ${aiAnalysisOptions.useGoogleAI ? '사용' : '미사용'}` +
      (aiAnalysisOptions.useGoogleAI ? ` (모델: ${aiAnalysisOptions.googleAIModel})` : '') +
      `\n\n이 옵션으로 분석을 업데이트하시겠습니까?`;
    
    if (!window.confirm(confirmMessage)) {
      return; // 취소
    }

    // 분석 실행
    await executeAiAnalysis();
  };

  // AI 분석 실행 (실제 분석 로직)
  const executeAiAnalysis = async () => {
    if (!selectedStock || !stockData) return;

    setLoading(true);
    try {
      // AI 거래 분석 엔드포인트 사용 (더 상세한 분석 결과 제공)
      const stockName = stockData.name || stockData.stock_name || selectedStock;
      const response = await authenticatedFetch(`/api/ai-trade/analyze/${selectedStock}`, {
        method: 'POST',
        timeout: 120000, // 120초 타임아웃 (Google AI 사용 시 시간이 오래 걸릴 수 있음)
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          stock_name: stockName,
          update_full_analysis_time: false, // 개별 분석이므로 전체 분석 시간 업데이트 안 함
          useGoogleAI: aiAnalysisOptions.useGoogleAI,
          useGemini3Flash: aiAnalysisOptions.googleAIModel === 'gemini-3-flash-preview',
          model: aiAnalysisOptions.useGoogleAI ? aiAnalysisOptions.googleAIModel : undefined
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AI 분석 완료:', data);
        
        // AI 분석 결과를 기존 형식으로 변환
        if (data.analysis) {
          const analysis = data.analysis;
          const recommendation = analysis.recommendation || {};
          
          // detailed_analysis 또는 summary를 reasoning으로 사용
          let reasoningText = '';
          if (analysis.detailed_analysis) {
            reasoningText = analysis.detailed_analysis;
          } else if (analysis.summary) {
            reasoningText = analysis.summary;
          } else if (recommendation.reasoning) {
            // recommendation.reasoning이 배열인 경우 문자열로 변환
            reasoningText = Array.isArray(recommendation.reasoning) 
              ? recommendation.reasoning.join('\n') 
              : recommendation.reasoning;
          }
          
          // recommendation 객체에 reasoning 추가 (UI에서 사용)
          const recommendationWithReasoning = {
            ...recommendation,
            reasoning: reasoningText || recommendation.reasoning || ''
          };
          
          // 분석 일자 추가
          const analysisDate = analysis.createdAt 
            ? new Date(analysis.createdAt).toLocaleString('ko-KR')
            : analysis.updatedAt 
              ? new Date(analysis.updatedAt).toLocaleString('ko-KR')
              : new Date().toLocaleString('ko-KR');
          
          setAiAnalysis({
            recommendation: recommendationWithReasoning,
            analysis: reasoningText || analysis.summary || '', // analysis 필드도 설정 (fallback용)
            confidence: recommendation.confidence || recommendation.confidence_score || 0,
            analysis_type: analysis.analysis_type || 'combined',
            local_ai: analysis.local_ai_analysis,
            google_ai: analysis.google_ai_analysis,
            // 추가 정보
            current_status: analysis.current_status,
            trade_config: analysis.trade_config,
            combined_analysis: analysis.combined_analysis,
            analysisDate: analysisDate, // 분석 일자 추가
            isExisting: false, // 새로 분석한 결과
            analysisMetadata: buildAnalysisMetadata(analysis, selectedStock, stockName)
          });
          
          // 분석 리스트 새로고침
          setTimeout(() => {
            if (typeof fetchAiAnalyses === 'function') {
              fetchAiAnalyses();
            }
          }, 1000);
          
          console.log('✅ AI 분석 결과 설정 완료:', {
            hasRecommendation: !!recommendationWithReasoning,
            hasReasoning: !!reasoningText,
            reasoningLength: reasoningText.length,
            confidence: recommendation.confidence || recommendation.confidence_score || 0,
            analysisType: analysis.analysis_type || 'combined'
          });
        } else {
          // analysis가 없는 경우 (예외 처리)
          console.warn('⚠️ AI 분석 결과에 analysis 필드가 없습니다:', data);
          setAiAnalysis({
            recommendation: data.recommendation || {},
            analysis: data.message || 'AI 분석이 완료되었습니다.',
            confidence: data.confidence || 0,
            analysisMetadata: buildAnalysisMetadata(
              { recommendation: data.recommendation, symbol: selectedStock, stock_name: stockName },
              selectedStock,
              stockName
            )
          });
        }
        
        // 질의 응답 초기화
        setAiQuestion('');
        setAiAnswer(null);
        
        // 성공 메시지
        alert(data.message || 'AI 분석이 완료되었습니다.');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('AI 분석 실패:', errorData.error);
        alert(errorData.error || 'AI 분석에 실패했습니다.');
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('AI 분석 실패:', error);
      if (error.name === 'TimeoutError') {
        alert('AI 분석 시간이 초과되었습니다. Google AI를 사용하지 않거나 잠시 후 다시 시도해주세요.');
      } else if (error.message === 'Authentication required') {
        setAuthError(true);
      } else {
        alert('AI 분석 중 오류가 발생했습니다: ' + (error.message || '알 수 없는 오류'));
      }
    } finally {
      setLoading(false);
    }
  };

  // AI 분석 결과에 대한 질의 응답 (분석 결과 있음/없음 모두 가능)
  const askAiQuestion = async () => {
    if (!aiQuestion || aiQuestion.trim() === '') {
      alert('질문을 입력해주세요.');
      return;
    }

    setAiQuestionLoading(true);
    try {
      const response = await authenticatedFetch('/api/ai/question', {
        method: 'POST',
        body: JSON.stringify({
          question: aiQuestion.trim(),
          symbol: selectedStock || null,
          marketData: stockData || null,
          analysisId: aiAnalysis?._id || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setAiAnswer({
          question: data.question,
          answer: data.answer,
          timestamp: data.timestamp
        });
        setAiQuestion(''); // 질문 입력 필드 초기화
        setSimpleExplanation(null); // 이전 해설 초기화
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`질의 응답 실패: ${errorData.error || '알 수 없는 오류'}`);
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('AI 질의 응답 실패:', error);
      alert('질의 응답 중 오류가 발생했습니다.');
      if (error.message === 'Authentication required') {
        setAuthError(true);
      }
    } finally {
      setAiQuestionLoading(false);
    }
  };

  // 쉬운 해설 생성
  const generateSimpleExplanation = async (text) => {
    if (!text || text.trim() === '') {
      alert('해설할 내용이 없습니다.');
      return;
    }

    setExplanationLoading(true);
    try {
      const response = await authenticatedFetch('/api/ai/explain', {
        method: 'POST',
        body: JSON.stringify({
          text: text.trim(),
          symbol: selectedStock || null,
          marketData: stockData || null,
          analysisId: aiAnalysis?._id || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSimpleExplanation({
          originalText: data.originalText,
          explanation: data.explanation,
          timestamp: data.timestamp
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        alert(`해설 생성 실패: ${errorData.error || '알 수 없는 오류'}`);
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('해설 생성 실패:', error);
      alert('해설 생성 중 오류가 발생했습니다.');
      if (error.message === 'Authentication required') {
        setAuthError(true);
      }
    } finally {
      setExplanationLoading(false);
    }
  };

  // 항목별 해석 생성
  const generateItemExplanation = async (itemKey, itemValue, itemType) => {
    const itemId = `${itemType}_${itemKey}`;
    
    // 이미 해석이 있으면 스킵
    if (itemExplanations[itemId]) {
      return;
    }

    setItemExplanationLoading(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await authenticatedFetch('/api/ai/explain-item', {
        method: 'POST',
        body: JSON.stringify({
          itemKey: itemKey,
          itemValue: itemValue,
          itemType: itemType,
          symbol: selectedStock || null,
          marketData: stockData || null
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setItemExplanations(prev => ({
          ...prev,
          [itemId]: {
            itemKey: data.itemKey,
            itemValue: data.itemValue,
            itemType: data.itemType,
            explanation: data.explanation,
            timestamp: data.timestamp
          }
        }));
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error(`항목 해석 생성 실패: ${errorData.error || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('항목 해석 생성 실패:', error);
    } finally {
      setItemExplanationLoading(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // AI 분석 초기화
  const resetAiAnalysis = () => {
    if (window.confirm('AI 분석 결과를 초기화하시겠습니까?')) {
      setAiAnalysis(null);
      setAiQuestion('');
      setAiAnswer(null);
      setAiQuestionLoading(false);
      setSimpleExplanation(null);
      setExplanationLoading(false);
      setItemExplanations({});
      setItemExplanationLoading({});
    }
  };

  // 거래시간 체크 함수
  const checkTradingHours = () => {
    const now = new Date();
    const koreaTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const hour = koreaTime.getHours();
    const minutes = koreaTime.getMinutes();
    const day = koreaTime.getDay(); // 0: 일요일, 6: 토요일
    
    // 평일(월~금) 9:00~15:30 거래시간 체크
    const isWeekday = day >= 1 && day <= 5;
    const isTradingHours = hour >= 9 && (hour < 15 || (hour === 15 && minutes <= 30));
    
    if (!isWeekday || !isTradingHours) {
      const dayNames = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];
      const dayName = dayNames[day];
      const timeStr = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      
      return {
        isValid: false,
        message: `거래시간이 아닙니다.\n거래시간: 평일(월~금) 09:00~15:30\n현재: ${dayName} ${timeStr}`
      };
    }
    
    return { isValid: true };
  };

  // 주문 실행
  const executeOrder = async () => {
    if (!selectedStock || !quantity || (!price && orderMethod === 'limit')) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    if (orderMethod === 'limit' && !price) {
      alert('지정가 주문은 가격을 입력해주세요.');
      return;
    }

    // 거래시간 체크 (사용자에게 경고만 표시, 서버에서도 체크)
    const tradingHoursCheck = checkTradingHours();
    if (!tradingHoursCheck.isValid) {
      const proceed = window.confirm(`${tradingHoursCheck.message}\n\n거래시간 외에도 주문을 시도하시겠습니까?`);
      if (!proceed) {
        return;
      }
    }

    setLoading(true);
    try {
      const response = await authenticatedFetch('/api/trading/order', {
        method: 'POST',
        body: JSON.stringify({
          symbol: selectedStock,
          order_type: orderType,
          quantity: parseInt(quantity),
          price: orderMethod === 'limit' ? parseFloat(price) : null,
          order_method: orderMethod, // limit, market
        }),
      });

      // 502, 503 에러 처리
      if (response.status === 502 || response.status === 503) {
        alert('서버 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      if (response.ok) {
        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          alert(`${orderType === 'buy' ? '매수' : '매도'} 주문이 완료되었습니다.\n주문번호: ${data.order_id || 'N/A'}`);
          // 포트폴리오 및 주문 내역 새로고침
          fetchPortfolio();
          fetchOrderHistory();
          fetchBalance();
          // 입력 필드 초기화
          setQuantity('');
          setPrice('');
        } else {
          alert('주문 응답 형식 오류가 발생했습니다.');
        }
      } else {
        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        let errorData;
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        } else {
          errorData = { error: '서버 연결 오류가 발생했습니다.' };
        }
        
        // 거래시간 관련 에러는 특별 처리
        const errorMessage = errorData.error || '주문 실패';
        if (errorMessage.includes('거래시간이 아닙니다')) {
          alert(`⚠️ ${errorMessage}\n\n거래시간: 평일(월~금) 09:00~15:30`);
        } else {
          alert(`주문 실패: ${errorMessage}`);
        }
        
        if (response.status === 401) {
          setAuthError(true);
        }
      }
    } catch (error) {
      console.error('주문 실행 실패:', error);
      if (error.message === 'Authentication required') {
        setAuthError(true);
        alert('로그인이 필요합니다.');
      } else if (error.message && error.message.includes('서버 연결 오류')) {
        alert('서버 연결 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      } else {
        alert('주문 실행 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 예약 주문 조회
  const fetchScheduledOrders = async () => {
    try {
      const response = await authenticatedFetch('/api/portfolio/scheduled-orders');
      if (response.ok) {
        const data = await response.json();
        // 취소된 주문은 제외하고 표시
        const activeOrders = (data.scheduledOrders || []).filter(order => order.status !== 'cancelled');
        setScheduledOrders(activeOrders);
      } else {
        console.error('예약 주문 조회 실패');
      }
    } catch (error) {
      console.error('예약 주문 조회 실패:', error);
    }
  };

  // 예약 주문 취소
  const cancelScheduledOrder = async (orderId) => {
    if (!window.confirm('예약 주문을 취소하시겠습니까?\n취소 후 보유종목 목록이 새로고침됩니다.')) {
      return;
    }
    try {
      const response = await authenticatedFetch(`/api/portfolio/scheduled-orders/${orderId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        alert('예약 주문이 취소되었습니다.');
        // 예약 주문 목록 새로고침
        await fetchScheduledOrders();
        // 보유종목 목록 새로고침 (예약 주문 취소로 인한 상태 변경 반영)
        await fetchPortfolio();
      } else {
        const error = await response.json();
        alert(error.error || '예약 주문 취소에 실패했습니다.');
      }
    } catch (error) {
      console.error('예약 주문 취소 실패:', error);
      alert('예약 주문 취소 중 오류가 발생했습니다.');
    }
  };

  // 자동거래 설정 조회
  const fetchAutoTrades = async (activeOnly = true) => {
    try {
      const url = activeOnly 
        ? '/api/statistics/auto-trade?active_only=true'
        : '/api/statistics/auto-trade';
      const response = await authenticatedFetch(url);
      if (response.ok) {
        const data = await response.json();
        setAutoTrades(data.autoTrades || []);
        console.log(`📋 자동거래 설정 조회 완료: ${data.autoTrades?.length || 0}건`);
      }
    } catch (error) {
      console.error('자동거래 설정 조회 실패:', error);
    }
  };

  // AI 거래 설정 조회 (모달 onSave에서 직접 호출하므로 여기서는 제거)
  // const fetchAiTradeConfig = async () => {
  //   try {
  //     const response = await authenticatedFetch('/api/ai-trade/config');
  //     if (response.ok) {
  //       const data = await response.json();
  //       setAiTradeConfig(data.config);
  //     }
  //   } catch (error) {
  //     console.error('AI 거래 설정 조회 실패:', error);
  //   }
  // };

  // AI 거래 상태 조회
  const fetchAiTradeStatus = async () => {
    try {
      console.log('🔍 [AI 거래] 상태 조회 시작...');
      const response = await authenticatedFetch('/api/ai-trade/status', {
        timeout: 120000 // 120초 - AI 상태/분석 관련
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [AI 거래] 상태 조회 성공:', {
          is_active: data.is_active,
          has_api_key: data.has_api_key,
          using_env_key: data.using_env_key,
          symbol_count: data.symbol_count,
          last_analysis_time: data.last_analysis_time
        });
        setAiTradeStatus({
          ...data,
          using_env_key: data.using_env_key || false
        });
        setServiceEnabled(!!data.is_active);
      } else {
        console.error('❌ [AI 거래] 상태 조회 실패:', response.status);
        const error = await response.json().catch(() => ({}));
        console.error('에러 상세:', error);
      }
    } catch (error) {
      console.error('❌ [AI 거래] 상태 조회 오류:', error);
    }
  };

  // AI 거래 계획 수정
  const updateTradePlan = async (analysisId, tradeConfig) => {
    try {
      const response = await authenticatedFetch(`/api/ai-trade/analyses/${analysisId}/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trade_config: tradeConfig })
      });

      if (response.ok) {
        const data = await response.json();
        alert('AI 거래 계획이 수정되었습니다.');
        setEditingPlan(null);
        setShowAnalysisDetail(false);
        // 분석 목록 새로고침 (필요한 경우)
        return data;
      } else {
        const error = await response.json();
        alert(error.error || '계획 수정에 실패했습니다.');
        throw new Error(error.error);
      }
    } catch (error) {
      console.error('AI 거래 계획 수정 실패:', error);
      throw error;
    }
  };

  const fetchPortfolio = async (forceRefresh = false) => {
    // 강제 새로고침이 아닌 경우에만 중복 호출 방지
    if (!forceRefresh) {
      // 중복 호출 방지 (1초 이내 중복 호출 차단)
      const now = Date.now();
      const lastFetch = lastFetchTime['portfolio'] || 0;
      if (now - lastFetch < 1000) {
        console.log('포트폴리오 조회: 중복 호출 방지');
        return;
      }
      
      // 이미 로딩 중이면 중복 호출 방지
      if (portfolioLoading) {
        console.log('포트폴리오 조회: 이미 로딩 중');
        return;
      }
      
      setLastFetchTime({ ...lastFetchTime, portfolio: now });
    }
    
    setPortfolioLoading(true);
    
    try {
      const url = forceRefresh ? '/api/portfolio?forceRefresh=true' : '/api/portfolio';
      const response = await authenticatedFetch(url, {
        timeout: 60000 // 60초 타임아웃 - KIS API 호출로 인해 시간이 오래 걸릴 수 있음
      });
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          console.error('포트폴리오 조회: HTML 반환됨 (API 라우팅/프록시 설정 확인 필요)');
          setPortfolio([]);
          setPortfolioLoading(false);
          return;
        }
        const data = await response.json();
        let portfolioData = [];
        if (Array.isArray(data.portfolio)) {
          portfolioData = data.portfolio;
        } else if (data.portfolio && Array.isArray(data.portfolio.holdings)) {
          portfolioData = data.portfolio.holdings;
        } else if (data.portfolio && typeof data.portfolio === 'object') {
          portfolioData = [];
        }
        
        // 포트폴리오 데이터 정규화 (필드명 통일)
        const normalizedPortfolio = portfolioData.map(item => {
          const symbol = (item.symbol || item.code || '').toUpperCase();
          
          // 종목명 통일 (여러 필드명 확인)
          let stockName = item.name || item.stock_name || item.stockName || '';
          
          // 종목명이 없거나 종목코드와 같으면 빈 문자열로 설정
          // 백엔드에서 이미 갱신을 시도했으므로, 여기서는 그대로 사용
          if (!stockName || stockName === symbol || stockName === item.symbol || stockName === item.code) {
            stockName = ''; // 종목명이 없으면 빈 문자열 (종목코드만 표시)
          }
          
          // 종목명이 있는 경우 공백 제거 및 검증
          if (stockName) {
            stockName = stockName.trim();
            // 종목명이 종목코드와 같거나 너무 짧으면 무효 처리
            if (stockName === symbol || stockName === item.symbol || stockName === item.code || stockName.length < 2) {
              stockName = '';
            }
          }
          
          // 평균가 통일 (averagePrice, avgPrice, avg_price, average_price)
          const avgPrice = item.averagePrice || item.avgPrice || item.avg_price || item.average_price || 0;
          
          // 현재가 통일 (currentPrice, current_price, price)
          const currentPrice = item.currentPrice || item.current_price || item.price || 0;
          
          // 수량
          const quantity = item.quantity || item.qty || 0;
          
          // 평가금액 계산
          const totalValue = quantity * currentPrice;
          
          // 손익 계산 (평균가와 현재가가 모두 있을 때)
          const profitLoss = (avgPrice > 0 && currentPrice > 0 && quantity > 0) 
            ? (currentPrice - avgPrice) * quantity 
            : (item.profit_loss !== undefined ? item.profit_loss : (item.profit !== undefined ? item.profit : 0));
          
          // 손익률 계산
          const profitLossRate = (avgPrice > 0 && currentPrice > 0) 
            ? ((currentPrice - avgPrice) / avgPrice) * 100 
            : (item.profit_loss_rate !== undefined ? item.profit_loss_rate : (item.profitRate !== undefined ? item.profitRate : 0));
          
          return {
            ...item,
            symbol: symbol,
            name: stockName,
            stock_name: stockName,
            stockName: stockName,
            quantity: quantity,
            averagePrice: avgPrice,
            avgPrice: avgPrice,
            avg_price: avgPrice,
            average_price: avgPrice,
            currentPrice: currentPrice,
            current_price: currentPrice,
            price: currentPrice,
            totalValue: totalValue,
            total_value: totalValue,
            profitLoss: profitLoss,
            profit_loss: profitLoss,
            profit: profitLoss,
            profitLossRate: profitLossRate,
            profit_loss_rate: profitLossRate,
            profitRate: profitLossRate
          };
        });
        
        // 디버깅: 포트폴리오 데이터 확인
        console.log('📥 포트폴리오 데이터 수신:', {
          항목수: normalizedPortfolio.length,
          샘플: normalizedPortfolio.length > 0 ? normalizedPortfolio[0] : null,
          원본데이터: data,
          정규화전: portfolioData.length > 0 ? portfolioData[0] : null
        });
        
        // 포트폴리오가 비어있을 때 경고 (실제로 보유 종목이 없을 수 있으므로 경고만 표시)
        if (normalizedPortfolio.length === 0) {
          console.log('ℹ️ 포트폴리오가 비어있습니다. 보유 종목이 없거나 KIS API 응답이 비어있을 수 있습니다.');
        }
        
        setPortfolio(normalizedPortfolio);
        setAuthError(false);
        // 포트폴리오 로드 후 자동거래 설정도 함께 로드
        fetchAutoTrades();
        // 예약 주문도 함께 로드
        fetchScheduledOrders();
      } else {
        // 502, 503 에러 처리
        if (response.status === 502 || response.status === 503) {
          console.error('서버 연결 오류:', response.status);
          return;
        }
        
        // Content-Type 확인 후 JSON 파싱
        const contentType = response.headers.get('content-type');
        let errorData;
        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        } else {
          errorData = { error: '서버 연결 오류가 발생했습니다.' };
        }
        
        if (response.status === 401) {
          setAuthError(true);
          console.log('인증이 필요합니다. 로그인해주세요.');
        } else {
          console.error('포트폴리오 조회 실패:', errorData.error);
        }
      }
    } catch (error) {
      if (error.message === 'Authentication required') {
        setAuthError(true);
        console.log('인증이 필요합니다. 로그인해주세요.');
      } else if (error.name === 'TimeoutError' || error.status === 504) {
        console.warn('⚠️ 포트폴리오 조회 타임아웃 - 잠시 후 다시 시도해주세요.');
      } else {
        console.error('포트폴리오 조회 실패:', error);
      }
      setPortfolio([]);
    } finally {
      setPortfolioLoading(false);
    }
  };

  // 잔고 조회 (API 호출 제한 방지)
  const fetchBalance = async (showAlert = false) => {
    // 중복 호출 방지 (1초 이내 중복 호출 차단)
    const now = Date.now();
    const lastFetch = lastFetchTime['balance'] || 0;
    if (now - lastFetch < 1000 && !showAlert) {
      console.log('잔고 조회: 중복 호출 방지');
      return;
    }
    
    // 이미 진행 중인 요청이 있으면 대기
    if (isFetching) {
      console.log('잔고 조회: 이미 진행 중인 요청이 있습니다');
      return;
    }
    
    setBalanceLoading(true);
    setBalanceError(null);
    setIsFetching(true);
    setLastFetchTime({ ...lastFetchTime, balance: now });
    
    try {
      const response = await authenticatedFetch('/api/portfolio/balance', {
        timeout: 60000 // 60초 타임아웃 - KIS API 호출로 인해 시간이 오래 걸릴 수 있음
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          console.error('잔고 조회: HTML 반환됨 (API 라우팅/프록시 설정 확인 필요)');
          setBalanceError('잔고 API 연결 설정을 확인해주세요. (서버/프록시)');
          setBalance(null);
          setBalanceLoading(false);
          setIsFetching(false);
          return;
        }
        const data = await response.json();
        const payload = data?.balance ?? data;
        setBalance(payload || null);
        // 서버에서 반환한 메시지(오류/안내)가 있으면 표시
        if (payload?.message) {
          setBalanceError(payload.message);
        } else {
          setBalanceError(null);
        }
        if (showAlert) {
          alert(payload?.message || '잔고 정보가 갱신되었습니다.');
        }
      } else {
        const errorData = await response.json().catch(() => ({ 
          error: '알 수 없는 오류가 발생했습니다.' 
        }));
        
        // 에러 타입별 처리
        let errorMessage = errorData.error || '잔고 조회에 실패했습니다.';
        
        if (response.status === 401) {
          errorMessage = errorData.error || '인증에 실패했습니다. 로그인 후 다시 시도해주세요.';
          setAuthError(true);
        } else if (response.status === 400) {
          // API 키 오류 등
          errorMessage = errorData.error || '설정을 확인해주세요.';
        } else if (response.status === 502 || response.status === 503) {
          errorMessage = '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.';
        }
        
        setBalanceError(errorMessage);
        setBalance(null);
        
        if (showAlert) {
          alert(errorMessage);
        }
        
        console.error('잔고 조회 실패:', {
          status: response.status,
          error: errorMessage,
          errorCode: errorData.errorCode
        });
      }
    } catch (error) {
      let errorMessage;
      if (error.name === 'TimeoutError' || error.status === 504) {
        errorMessage = '잔고 조회 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.message?.includes('서버 연결 오류')) {
        errorMessage = '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.';
      } else {
        errorMessage = '잔고 조회 중 오류가 발생했습니다.';
      }
      
      setBalanceError(errorMessage);
      setBalance(null);
      
      if (showAlert) {
        alert(errorMessage);
      }
      
      console.error('잔고 조회 실패:', error);
    } finally {
      setBalanceLoading(false);
      setIsFetching(false);
    }
  };

  const buildPerformanceParams = (range) => {
    const params = new URLSearchParams();
    if (range?.type === 'custom') {
      if (range.startDate) {
        params.set('startDate', range.startDate);
      }
      if (range.endDate) {
        params.set('endDate', range.endDate);
      }
    } else if (range?.type === 'month') {
      params.set('period', '30d');
    } else if (range?.type === 'quarter') {
      params.set('period', '90d');
    } else {
      params.set('period', '7d');
    }
    return params.toString();
  };

  const fetchPerformanceStats = async (rangeOverride) => {
    const range = rangeOverride || performanceRange;
    setPerformanceLoading(true);
    setPerformanceError(null);
    try {
      const query = buildPerformanceParams(range);
      const response = await authenticatedFetch(`/api/ai-trade/performance${query ? `?${query}` : ''}`, {
        timeout: 60000
      });
      if (response.ok) {
        const data = await response.json();
        setPerformanceStats(data.performance || null);
      } else {
        const errorData = await response.json().catch(() => ({ error: '알 수 없는 오류가 발생했습니다.' }));
        setPerformanceError(errorData.error || '거래실적 조회에 실패했습니다.');
        setPerformanceStats(null);
      }
    } catch (error) {
      setPerformanceError('거래실적 조회 중 오류가 발생했습니다.');
      setPerformanceStats(null);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const handleTogglePerformance = () => {
    setPerformanceVisible(prev => {
      const next = !prev;
      if (next) {
        fetchPerformanceStats();
      }
      return next;
    });
  };

  const handleSelectPerformanceRange = (type) => {
    const nextRange = { ...performanceRange, type };
    setPerformanceRange(nextRange);
    fetchPerformanceStats(nextRange);
  };

  const handleApplyCustomPerformanceRange = () => {
    if (!performanceRange.startDate || !performanceRange.endDate) {
      setPerformanceError('시작일과 종료일을 모두 입력해주세요.');
      return;
    }
    const nextRange = { ...performanceRange, type: 'custom' };
    setPerformanceRange(nextRange);
    fetchPerformanceStats(nextRange);
  };

  // 주문 내역: useOrderHistory 훅 사용

  const handleLoginSuccess = async (userData, token) => {
    console.log('✅ 로그인 성공 - 사용자 정보 설정:', {
      hasUserData: !!userData,
      hasToken: !!token,
      userNickname: userData?.nickname,
      userEmail: userData?.email,
      hasTrading: !!userData?.trading
    });
    
    // 로그인 성공 시 즉시 사용자 정보 설정
    const initialUserData = {
      ...userData,
      trading: userData.trading || {}
    };
    setUser(initialUserData);
    setAuthError(false);
    setShowLogin(false);
    
    // 로그인 성공 시 소개 페이지에서 거래 탭으로 이동
    if (activeTab === 'intro') {
      setActiveTab('trade');
    }
    
    console.log('👤 초기 사용자 정보 설정 완료:', {
      hasUser: !!initialUserData,
      hasNickname: !!initialUserData.nickname,
      hasTrading: !!initialUserData.trading
    });
    
    // 사용자 정보 다시 가져오기 (최신 정보)
    try {
      console.log('🔍 최신 사용자 정보 조회 시작...');
      await fetchUserInfo();
      console.log('✅ 최신 사용자 정보 조회 완료');
    } catch (error) {
      console.error('❌ 사용자 정보 조회 실패 (로그인 후):', error);
      // 에러가 있어도 로그인은 성공한 상태이므로 계속 진행
      // 초기 사용자 정보는 이미 설정되어 있음
    }
  };

  // 로그인 확인 및 사용자 정보 조회
  useEffect(() => {
    const initUserInfo = async () => {
    const token = getToken();
    if (!token) {
      console.log('⚠️ 토큰 없음 - 로그인 필요');
      setAuthError(true);
      setShowLogin(true);
      setUser(null);
    } else {
      console.log('🔍 토큰 있음 - 사용자 정보 조회 시작');
      // 토큰이 있으면 사용자 정보 조회 시도
      try {
        await fetchUserInfo();
      } catch (error) {
        console.error('❌ 사용자 정보 조회 실패:', error);
        
        // Rate limit 에러는 fetchUserInfoFromAuth가 캐시를 처리하므로
        // 여기서는 추가 처리 불필요 (캐시된 데이터가 있으면 이미 설정됨)
        if (error.message && error.message.includes('Rate limit')) {
          console.warn('⚠️ Rate limit 발생, 캐시된 데이터 확인 중...');
          // fetchUserInfoFromAuth가 캐시를 처리하므로 추가 대기
          setTimeout(async () => {
            try {
              await fetchUserInfo(true); // 강제 새로고침 시도
            } catch (retryError) {
              console.error('❌ 재시도 실패:', retryError);
            }
          }, 5000); // 5초 후 재시도
          return;
        }
        
        // 401 에러면 토큰이 만료된 것
        if (error.message === 'Authentication required' || error.message?.includes('401')) {
          setAuthError(true);
          setShowLogin(true);
          setUser(null);
        } else {
          // 기타 에러는 일시적일 수 있으므로 로그인 화면은 표시하지 않음
          // (서버 연결 오류 등)
          console.warn('⚠️ 사용자 정보 조회 실패 (일시적 오류 가능):', error.message);
        }
      }
    }
    };
    
    initUserInfo();
  }, []);

  // DB 연결 상태 확인
  const checkDbConnection = async () => {
    try {
      // authenticatedFetch를 사용하여 trading-service의 health 엔드포인트 호출
      // skipAuthHandling을 true로 설정하여 401 에러 시에도 처리 가능하도록 함
      const response = await authenticatedFetch('/api/health', {
        timeout: 10000, // 10초 타임아웃
        skipAuthHandling: true // 인증 실패 시에도 에러 처리하지 않음
      });
      
      // Content-Type 확인 (HTML 응답 방지)
      const contentType = response.headers.get('content-type');
      const isHTMLResponse = contentType && contentType.includes('text/html');
      
      if (isHTMLResponse) {
        console.error('❌ Health check HTML 응답 (라우팅 문제):', {
          url: response.url,
          status: response.status
        });
        setDbConnectionStatus({ 
          status: 'error', 
          message: '⚠️ 서버 라우팅 오류 (nginx 설정 확인 필요)' 
        });
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        const dbStatus = data.database?.status || 'unknown';
        const readyState = data.database?.readyState || 0;
        
        if (dbStatus === 'connected' && readyState === 1) {
          setDbConnectionStatus({ 
            status: 'connected', 
            message: '✅ DB 연결 정상' 
          });
        } else {
          setDbConnectionStatus({ 
            status: 'disconnected', 
            message: '⚠️ 데이터베이스 연결 오류' 
          });
        }
      } else {
        // 401 에러는 인증이 필요한 것이므로 정상 (health check는 공개 엔드포인트여야 함)
        if (response.status === 401) {
          console.warn('⚠️ Health check 인증 필요 (정상일 수 있음)');
          setDbConnectionStatus({ 
            status: 'unknown', 
            message: '⚠️ 서버 상태 확인 불가 (인증 필요)' 
          });
        } else {
          console.warn('⚠️ Health check 응답 실패:', response.status, response.statusText);
          setDbConnectionStatus({ 
            status: 'error', 
            message: '⚠️ 서버 상태 확인 실패' 
          });
        }
      }
    } catch (error) {
      console.error('❌ DB 연결 상태 확인 실패:', error);
      // HTML 응답 에러 처리 (라우팅 문제)
      if (error.message && (error.message.includes('Unexpected token') || error.message.includes('<!doctype'))) {
        setDbConnectionStatus({ 
          status: 'error', 
          message: '⚠️ 서버 라우팅 오류 (nginx 설정 확인 필요)' 
        });
      } else if (error.name === 'TimeoutError' || error.name === 'AbortError') {
        setDbConnectionStatus({ 
          status: 'error', 
          message: '⚠️ 서버 응답 시간 초과 (trading-service 확인 필요)' 
        });
      } else if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setDbConnectionStatus({ 
          status: 'error', 
          message: '⚠️ 서버 연결 불가 (trading-service 확인 필요)' 
        });
      } else {
        setDbConnectionStatus({ 
          status: 'error', 
          message: '⚠️ 서버 연결 실패' 
        });
      }
    }
  };

  // 사용자 정보 조회 (auth.js의 캐싱된 fetchUserInfo 사용)
  const fetchUserInfo = async (forceRefresh = false) => {
    try {
      console.log('🔍 Fetching user info...', { forceRefresh });
      const token = getToken();
      if (!token) {
        console.log('⚠️ No token found');
        setAuthError(true);
        setShowLogin(true);
        setUser(null);
        return;
      }

      const userData = await fetchUserInfoFromAuth(forceRefresh);
      
      if (userData) {
        console.log('✅ User info fetched:', {
          userId: userData.id,
          email: userData.email,
          nickname: userData.nickname,
          name: userData.name,
          hasTrading: !!userData.trading,
          isPaperTrading: userData.trading?.is_paper_trading,
          hasApiKey: !!userData.trading?.paper_app_key || !!userData.trading?.real_app_key,
          paperHtsId: userData.trading?.paper_hts_id,
          realHtsId: userData.trading?.real_hts_id
        });
        
        const processedUserData = {
          ...userData,
          trading: userData.trading || {}
        };
        console.log('✅ 사용자 정보 설정:', {
          hasUser: !!processedUserData,
          hasNickname: !!processedUserData.nickname,
          hasName: !!processedUserData.name,
          hasEmail: !!processedUserData.email,
          hasTrading: !!processedUserData.trading,
          isPaperTrading: processedUserData.trading?.is_paper_trading,
          hasServices: !!processedUserData.services,
          userKeys: Object.keys(processedUserData)
        });
        
        // 사용자 정보가 유효한지 확인
        if (processedUserData.nickname || processedUserData.name || processedUserData.email) {
          setUser(processedUserData);
          setAuthError(false);
          setShowLogin(false);
          console.log('✅ 사용자 정보 상태 업데이트 완료');
        } else {
          console.warn('⚠️ 사용자 정보가 불완전함:', processedUserData);
          // 불완전한 정보라도 설정 (나중에 업데이트될 수 있음)
          setUser(processedUserData);
          setAuthError(false);
        }
      } else {
        // 사용자 정보가 null이면 인증 실패
        console.error('❌ 사용자 정보 조회 실패 - 인증 필요');
        setAuthError(true);
        setShowLogin(true);
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Fetch user info error:', error);
      
      // Rate limit 에러는 fetchUserInfoFromAuth가 이미 캐시를 처리하므로
      // 여기서는 추가 처리 불필요
      
      // 서버 연결 오류는 일시적이므로 기존 사용자 정보 유지
      if (error.message && error.message.includes('서버 연결 오류')) {
        console.error('❌ 서버 연결 오류:', error.message);
        // 기존 사용자 정보가 있으면 유지
        if (user) {
          return;
        }
      }
      
      // 다른 에러는 인증 문제로 간주
      setAuthError(true);
      setShowLogin(true);
      setUser(null);
    }
  };

  useEffect(() => {
    if (selectedStock && !authError) {
      // 종목 선택 시 stockData 초기화 (null 방지)
      setStockData(prev => ({
        symbol: selectedStock,
        name: prev?.name || '',
        stock_name: prev?.stock_name || '',
        price: prev?.price || 0,
        change: prev?.change || 0,
        changeRate: prev?.changeRate || 0,
        changePercent: prev?.changePercent || 0,
        volume: prev?.volume || 0,
        open: prev?.open || 0,
        high: prev?.high || 0,
        low: prev?.low || 0
      }));
      fetchStockData(selectedStock);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, authError]);

  // AI 거래 분석 리스트 조회 (진행 중인 요청이 있으면 같은 Promise 재사용하여 중복 호출 방지)
  const fetchAiAnalyses = async () => {
    // 토큰 확인 (로그인하지 않은 경우 조용히 스킵)
    const token = getToken();
    if (!token) {
      console.log('⚠️ 토큰 없음 - AI 분석 리스트 조회 스킵');
      setAiAnalyses([]);
      return;
    }
    // 진행 중인 요청이 있으면 같은 Promise 재사용
    const inFlight = fetchAiAnalysesInFlightRef.current;
    if (inFlight && typeof inFlight.then === 'function') {
      return inFlight;
    }
    // 중복 호출 방지 (1초 이내 중복 호출 차단)
    const now = Date.now();
    const lastFetch = lastFetchTime['aiAnalyses'] || 0;
    if (now - lastFetch < 1000) {
      console.log('AI 분석 리스트 조회: 중복 호출 방지');
      return;
    }
    setLastFetchTime({ ...lastFetchTime, aiAnalyses: now });

    const run = async () => {
      try {
        const response = await authenticatedFetch('/api/ai-trade/analyses', {
          timeout: 120000 // 120초 타임아웃 - 리스트 조회 완료까지 여유
        });
        if (response.ok) {
          const data = await response.json();
          const analyses = data.analyses || [];
          setAiAnalyses(analyses);
          setCurrentPage(1); // 새로고침 시 첫 페이지로
          console.log('📊 AI 분석 결과 조회:', {
            총개수: analyses.length,
            종목코드목록: analyses.slice(0, 10).map(a => a.symbol),
            샘플: analyses.length > 0 ? {
              symbol: analyses[0].symbol,
              stock_name: analyses[0].stock_name,
              recommendation: analyses[0].recommendation,
              createdAt: analyses[0].createdAt
            } : null
          });
        } else {
          if (response.status === 401) {
            console.log('⚠️ 로그인 필요 - AI 분석 리스트 조회 스킵');
            setAiAnalyses([]);
            return;
          }
          const errorData = await response.json().catch(() => ({ error: '알 수 없는 오류' }));
          console.error('AI 분석 리스트 조회 실패:', errorData.error);
          setAiAnalyses([]);
        }
      } catch (error) {
        if (error.message === 'Authentication required' || error.message?.includes('401')) {
          console.log('⚠️ 로그인 필요 - AI 분석 리스트 조회 스킵');
          setAiAnalyses([]);
          return;
        }
        console.error('AI 분석 리스트 조회 오류:', error);
        setAiAnalyses([]);
      }
    };
    const promise = run();
    fetchAiAnalysesInFlightRef.current = promise;
    promise.finally(() => {
      if (fetchAiAnalysesInFlightRef.current === promise) {
        fetchAiAnalysesInFlightRef.current = null;
      }
    });
    return promise;
  };

  // 관심종목 조회 (watchlist - AI분석종목 탭에서 사용)
  const fetchWatchlist = async () => {
    // 로그인 상태 확인
    const token = getToken();
    if (!token) {
      console.log('⚠️ 토큰 없음 - 관심종목 조회 스킵');
      return;
    }
    
    // 중복 호출 방지 (1초 이내 중복 호출 차단)
    const now = Date.now();
    const lastFetch = lastFetchTime['watchlist'] || 0;
    if (now - lastFetch < 1000) {
      console.log('관심종목 조회: 중복 호출 방지');
      return;
    }
    
    setLastFetchTime({ ...lastFetchTime, watchlist: now });
    
    try {
      const response = await authenticatedFetch('/api/watchlist', {
        timeout: 60000 // 60초 타임아웃 - 여러 관심종목 조회로 인해 시간이 오래 걸릴 수 있음
      });
      if (response.ok) {
        const data = await response.json();
        const watchlistData = data.watchlist || [];
        setWatchlist(watchlistData);
        
        // watchlistStocksList도 업데이트 (종목조회 탭용)
        const stocksList = watchlistData.map(item => ({
          symbol: item.symbol,
          name: item.name || item.stock_name || item.symbol,
          stock_name: item.stock_name || item.name || item.symbol,
          market: item.market || '',
          _id: item._id
        }));
        setWatchlistStocksList(stocksList);
        console.log('✅ 관심종목 리스트 업데이트:', stocksList.length, '개');
      } else {
        console.error('관심종목 조회 실패');
        setWatchlistStocksList([]);
      }
    } catch (error) {
      // 401 에러는 로그인 필요하므로 조용히 처리
      if (error.message?.includes('Authentication required') || error.message?.includes('401')) {
        console.log('⚠️ 로그인 필요 - 관심종목 조회 스킵');
        setWatchlistStocksList([]);
        return;
      }
      console.error('관심종목 조회 오류:', error);
      setWatchlistStocksList([]);
    }
  };

  // 즐겨찾기 종목 조회
  const fetchFavoriteStocks = async () => {
    // 로그인 상태 확인
    const token = getToken();
    if (!token) {
      console.log('⚠️ 토큰 없음 - 즐겨찾기 종목 조회 스킵');
      return;
    }
    
    try {
      const response = await authenticatedFetch('/api/user-favorite-stocks', {
        timeout: 60000 // 60초 타임아웃 - 여러 즐겨찾기 종목 조회로 인해 시간이 오래 걸릴 수 있음
      });
      if (response.ok) {
        const data = await response.json();
        const favoriteStocks = data.favoriteStocks || [];
        console.log('즐겨찾기 종목 리스트:', favoriteStocks);
        
        // 종목 리스트로 변환
        const stocks = favoriteStocks.map(fav => ({
          symbol: fav.symbol,
          name: fav.stock_name || fav.symbol,
          stock_name: fav.stock_name || fav.symbol,
          market: fav.market || '',
          favorite: fav,
          _id: fav._id
        }));
        
        setFavoriteStocksList(stocks);
      } else {
        console.error('즐겨찾기 종목 조회 실패');
        setFavoriteStocksList([]);
      }
    } catch (error) {
      // 401 에러는 로그인 필요하므로 조용히 처리
      if (error.message?.includes('Authentication required') || error.message?.includes('401')) {
        console.log('⚠️ 로그인 필요 - 즐겨찾기 종목 조회 스킵');
        setFavoriteStocksList([]);
        return;
      }
      console.error('즐겨찾기 종목 조회 오류:', error);
      setFavoriteStocksList([]);
    }
  };

  // AI 추천 종목 조회 (symbol_configs와 analyses 병합)
  const fetchAiRecommendedStocks = async () => {
    // 로그인 상태 확인
    const token = getToken();
    if (!token) {
      console.log('⚠️ 토큰 없음 - AI 추천 종목 조회 스킵');
      return;
    }
    
    // 중복 호출 방지 (1초 이내 중복 호출 차단)
    const now = Date.now();
    const lastFetch = lastFetchTime['aiRecommendedStocks'] || 0;
    if (now - lastFetch < 1000) {
      console.log('AI 추천 종목 조회: 중복 호출 방지');
      return;
    }
    
    setLastFetchTime({ ...lastFetchTime, aiRecommendedStocks: now });
    
    try {
      // 1. symbol_configs 조회 (AITradeConfig에서)
      let symbolConfigs = [];
      try {
        const configResponse = await authenticatedFetch('/api/ai-trade/config', {
          timeout: 120000 // 120초 - AI 설정/분석 관련
        });
        if (configResponse.ok) {
          const configData = await configResponse.json();
          symbolConfigs = configData.config?.symbol_configs || [];
          console.log('📋 AI 거래 설정 종목 (symbol_configs):', symbolConfigs.length, '개');
        }
      } catch (error) {
        console.warn('⚠️ AI 거래 설정 조회 실패:', error);
      }
      
      // 2. analyses 조회 (AITradeAnalysis에서)
      const response = await authenticatedFetch('/api/ai-trade/analyses?status=관찰중', {
        timeout: 120000 // 120초 - 분석 리스트 조회
      });
      let analyses = [];
      if (response.ok) {
        const data = await response.json();
        analyses = data.analyses || [];
        console.log('📊 AI 분석 결과 (analyses):', analyses.length, '개');
      }
      
      // 3. symbol_configs와 analyses 병합
      const symbolMap = new Map();
      
      // symbol_configs를 먼저 추가 (최신 설정 정보)
      symbolConfigs.forEach(symbolConfig => {
        if (symbolConfig.symbol && /^\d{6}$/.test(symbolConfig.symbol)) {
          symbolMap.set(symbolConfig.symbol, {
            symbol: symbolConfig.symbol,
            name: symbolConfig.stock_name || symbolConfig.symbol,
            symbolConfig: symbolConfig,
            analysis: null,
            hasScheduledOrder: (symbolConfig.scheduled_orders && symbolConfig.scheduled_orders.length > 0)
          });
        }
      });
      
      // analyses를 병합 (기존 항목에 분석 정보 추가)
      analyses.forEach(analysis => {
        if (analysis.symbol && /^\d{6}$/.test(analysis.symbol)) {
          const existing = symbolMap.get(analysis.symbol);
          if (existing) {
            existing.analysis = analysis;
          } else {
            symbolMap.set(analysis.symbol, {
              symbol: analysis.symbol,
              name: analysis.stock_name || analysis.name || analysis.symbol,
              symbolConfig: null,
              analysis: analysis,
              hasScheduledOrder: false
            });
          }
        }
      });
      
      // 4. 종목명 조회 (없는 경우)
      const stocks = await Promise.all(Array.from(symbolMap.values()).map(async (stock) => {
        let stockName = stock.name;
        
        // 종목명이 없거나 종목코드와 같으면 조회
        if (!stockName || stockName === stock.symbol) {
          try {
            const stockResponse = await authenticatedFetch(`/api/trading/stock/${stock.symbol}`);
            if (stockResponse.ok) {
              const stockData = await stockResponse.json();
              stockName = stockData.name || stock.symbol;
            }
          } catch (error) {
            console.error(`종목명 조회 실패 (${stock.symbol}):`, error);
          }
        }
        
        return {
          ...stock,
          name: stockName || stock.symbol
        };
      }));
      
      console.log('✅ AI 추천 종목 리스트 (병합 완료):', stocks.length, '개', {
        symbolConfigsOnly: stocks.filter(s => s.symbolConfig && !s.analysis).length,
        analysesOnly: stocks.filter(s => s.analysis && !s.symbolConfig).length,
        both: stocks.filter(s => s.symbolConfig && s.analysis).length,
        withScheduledOrder: stocks.filter(s => s.hasScheduledOrder).length
      });
      
      setAiRecommendedStocksList(stocks);
    } catch (error) {
      // 401 에러는 로그인 필요하므로 조용히 처리
      if (error.message?.includes('Authentication required') || error.message?.includes('401')) {
        console.log('⚠️ 로그인 필요 - AI 추천 종목 조회 스킵');
        return;
      }
      console.error('AI 추천 종목 조회 오류:', error);
    }
  };

  // ai-trade 탭 활성화 시 분석 리스트 자동 조회
  useEffect(() => {
    if (!authError && activeTab === 'ai-trade') {
      fetchAiAnalyses();
      fetchAiTradeStatus();
      fetchWatchlist();
    }
    if (!authError && activeTab === 'history') {
      fetchAiTradeConfig(false);
    }
  }, [activeTab, authError, fetchAiTradeConfig]);

  // trade 탭 활성화 시에도 AI 분석 결과 조회 (종목 선택 시 연동을 위해)
  useEffect(() => {
    if (!authError && activeTab === 'trade') {
      // AI 분석 결과가 없으면 조회
      if (!aiAnalyses || aiAnalyses.length === 0) {
        fetchAiAnalyses();
      }
    }
  }, [activeTab, authError, aiAnalyses]);

  // 주문 탭 활성화 시 AI 추천 종목, 관심종목, 즐겨찾기 조회
  useEffect(() => {
    // 로그인 상태 확인 (토큰이 있어야 함)
    const token = getToken();
    if (!authError && activeTab === 'trade' && token && user) {
      fetchWatchlist(); // AI분석종목 탭용 (watchlist)
      fetchFavoriteStocks(); // 즐겨찾는 종목 탭용
    }
  }, [activeTab, authError, user]);

  // 비로그인 시 소개 페이지로 자동 이동 (로그인 시에는 거래 유지)
  useEffect(() => {
    const token = getToken();
    if (!token || authError) {
      if (activeTab !== 'intro') setActiveTab('intro');
    }
  }, [user, authError]);

  // 인기종목 리스트 초기화 (컴포넌트 마운트 시)
  useEffect(() => {
    if (popularStocks && popularStocks.length > 0) {
      const stocksList = popularStocks.map(stock => ({
        symbol: stock.symbol,
        name: stock.name,
        stock_name: stock.name,
        market: stock.market || ''
      }));
      setPopularStocksList(stocksList);
      console.log('✅ 인기종목 리스트 초기화:', stocksList.length, '개');
    }
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  // 탭 변경 시 종목 리스트 업데이트
  useEffect(() => {
    // 로그인 상태 확인 (토큰이 있어야 함)
    const token = getToken();
    if (!token || !user) {
      // 로그인하지 않은 경우 인기종목만 표시
      if (popularStocksTab === 'popular' && popularStocks && popularStocks.length > 0) {
        const stocksList = popularStocks.map(stock => ({
          symbol: stock.symbol,
          name: stock.name,
          stock_name: stock.name,
          market: stock.market || ''
        }));
        setPopularStocksList(stocksList);
      }
      return;
    }
    
    if (popularStocksTab === 'popular') {
      // 인기종목 탭 -> 기본 인기종목 표시
      if (popularStocks && popularStocks.length > 0) {
        const stocksList = popularStocks.map(stock => ({
          symbol: stock.symbol,
          name: stock.name,
          stock_name: stock.name,
          market: stock.market || ''
        }));
        setPopularStocksList(stocksList);
      }
    } else if (popularStocksTab === 'ai-recommend') {
      // AI분석종목 탭 -> AI 추천 종목 조회
      fetchAiRecommendedStocks();
    } else if (popularStocksTab === 'watchlist') {
      // 관심종목 탭 -> watchlist 데이터 표시
      fetchWatchlist();
    } else if (popularStocksTab === 'favorite') {
      // 즐겨찾는 종목 탭 -> 즐겨찾기 데이터 표시
      fetchFavoriteStocks();
    }
  }, [popularStocksTab, user]);

  // 자동 갱신 제거 - 사용자가 명시적으로 새로고침 버튼을 클릭할 때만 갱신
  // useEffect(() => {
  //   if (!authError) {
  //     fetchPortfolio();
  //     fetchBalance();
  //     fetchOrderHistory();
  //   }
  // }, [authError]);

  // 탭 변경 시에도 자동 갱신하지 않음 - 새로고침 버튼으로만 갱신
  // useEffect(() => {
  //   if (!authError && activeTab === 'portfolio') {
  //     fetchPortfolio();
  //   } else if (!authError && activeTab === 'balance') {
  //     fetchBalance();
  //   } else if (!authError && activeTab === 'history') {
  //     fetchOrderHistory();
  //   }
  // }, [activeTab, authError]);

  // Trading 페이지에서 헤더가 항상 보이도록 body에 데이터 속성 추가
  // DB 연결 상태는 최초 1회만 확인; 이후는 checkTradingService(60초 주기)에서 health 응답으로 갱신
  useEffect(() => {
    document.body.setAttribute('data-path', '/trading');
    document.documentElement.setAttribute('data-path', '/trading');
    checkDbConnection();
    return () => {
      document.body.removeAttribute('data-path');
      document.documentElement.removeAttribute('data-path');
    };
  }, []);

  // 사용자 정보 디버깅 및 자동 재조회
  useEffect(() => {
    console.log('👤 사용자 정보 상태:', {
      hasUser: !!user,
      userNickname: user?.nickname,
      userName: user?.name,
      userEmail: user?.email,
      hasTrading: !!user?.trading,
      isPaperTrading: user?.trading?.is_paper_trading,
      hasServices: !!user?.services,
      services: user?.services,
      userObject: user ? Object.keys(user) : null
    });
    
    // 사용자 정보가 없는데 토큰이 있는 경우 재조회 시도 (debounce 적용)
    if (!user && getToken()) {
      console.log('⚠️ 사용자 정보가 없지만 토큰이 있음 - 재조회 시도');
      // 중복 요청 방지를 위해 약간의 지연 후 재조회
      const timeoutId = setTimeout(() => {
        fetchUserInfo().catch(error => {
          console.error('❌ 사용자 정보 재조회 실패:', error);
          // Rate limit 에러는 나중에 재시도
          if (error.message && error.message.includes('Rate limit')) {
            console.warn('⚠️ Rate limit으로 인한 재조회 실패, 나중에 재시도 예정');
          } else {
            // 다른 에러는 로그인 화면 표시
            setAuthError(true);
            setShowLogin(true);
          }
        });
      }, 1000); // 1초 후 재조회
      
      return () => clearTimeout(timeoutId);
    }
  }, [user]);

  // --- trading-service 헬스 체크 / 시장 상태 (토큰·API 반복 호출 제한) ---
  const HEALTH_CHECK_MIN_INTERVAL_MS = 45000; // 45초 이내 재호출 방지 (effect 재실행·다중 구독 시 연타 완화)
  const healthCheckRef = useRef({ inFlight: false, lastRunAt: 0 });

  const checkTradingService = useCallback(async () => {
    const now = Date.now();
    if (healthCheckRef.current.inFlight) return;
    if (now - healthCheckRef.current.lastRunAt < HEALTH_CHECK_MIN_INTERVAL_MS) return;
    healthCheckRef.current.inFlight = true;
    healthCheckRef.current.lastRunAt = now;

    try {
      const response = await authenticatedFetch('/api/health', {
        timeout: 5000,
        skipAuthHandling: true
      });
      if (response.ok) {
        const data = await response.json();
        const status = data.status === 'ok' ? '실행중' : '문제';
        setTradingServiceStatus({ status, info: data, lastChecked: now });
        // DB 연결 상태를 health 응답으로 통합 갱신 (별도 /api/health 반복 호출 제거)
        const dbStatus = data.database?.status;
        const readyState = data.database?.readyState;
        if (dbStatus === 'connected' && readyState === 1) {
          setDbConnectionStatus({ status: 'connected', message: '✅ DB 연결 정상' });
        } else if (dbStatus != null) {
          setDbConnectionStatus({ status: 'disconnected', message: '⚠️ 데이터베이스 연결 오류' });
        }
      } else {
        setTradingServiceStatus({ status: '중단', info: null, lastChecked: now });
      }
    } catch (error) {
      console.error('trading-service health check failed:', error.message || error);
      setTradingServiceStatus({ status: '중단', info: { error: error.message }, lastChecked: now });
    } finally {
      healthCheckRef.current.inFlight = false;
    }

    const token = getToken();
    if (!token || !user) return;

    try {
      const statusResponse = await authenticatedFetch('/api/ai-trade/status', { timeout: 10000 });
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        const active = !!statusData.is_active;
        setServiceEnabled(active);
        setAiTradeStatus(prev => ({ ...prev, ...statusData, using_env_key: statusData.using_env_key || false }));
        setTradingServiceStatus(prev => ({
          ...prev,
          status: active ? prev.status : '중단',
          info: { ...(prev.info || {}), ai_trade: statusData },
          lastChecked: Date.now()
        }));
      }
    } catch (statusError) {
      console.warn('AI trade status check failed:', statusError.message || statusError);
    }
  }, [user]);

  const toggleTradingService = async () => {
    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }
    if (user?.email !== 'begite@naver.com') {
      alert('관리자만 변경할 수 있습니다.');
      return;
    }
    const nextEnabled = !serviceEnabled;
    const confirmMessage = nextEnabled
      ? 'trading-service를 실행 상태로 전환하시겠습니까?'
      : 'trading-service를 중단 상태로 전환하시겠습니까?\n중단 시 자동거래/AI 스케줄러가 실행되지 않습니다.';
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setServiceToggleLoading(true);
    try {
      const response = await authenticatedFetch('/api/ai-trade/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          is_active: nextEnabled
        })
      });
      if (response.ok) {
        const data = await response.json();
        const active = !!data.config?.is_active;
        setServiceEnabled(active);
        setAiTradeStatus(prev => ({
          ...prev,
          is_active: active
        }));
        setTradingServiceStatus(prev => ({
          ...prev,
          status: active ? prev.status : '중단',
          info: { ...(prev.info || {}), ai_trade: { ...(prev.info?.ai_trade || {}), is_active: active } }
        }));
      } else {
        const error = await response.json().catch(() => ({}));
        alert(error.error || '서비스 상태 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('서비스 토글 실패:', error);
      alert('서비스 상태 변경 중 오류가 발생했습니다.');
    } finally {
      setServiceToggleLoading(false);
      checkTradingService();
    }
  };

  // 한국 공휴일 목록 (간단히 2024-2025년 포함)
  const KOREAN_HOLIDAYS_2024 = [
    '2024-01-01', '2024-03-01', '2024-05-05', '2024-05-06',
    '2024-06-06', '2024-08-15', '2024-09-16', '2024-09-17', '2024-09-18',
    '2024-10-03', '2024-10-09', '2024-12-25', '2024-12-31'
  ];
  const KOREAN_HOLIDAYS_2025 = [
    '2025-01-01', '2025-01-28', '2025-01-29', '2025-01-30',
    '2025-03-01', '2025-05-05', '2025-05-06', '2025-06-06',
    '2025-08-15', '2025-10-03', '2025-10-06', '2025-10-07', '2025-10-08',
    '2025-10-09', '2025-12-25'
  ];

  const isHoliday = (date = new Date()) => {
    const koreaTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const year = koreaTime.getFullYear();
    const month = String(koreaTime.getMonth() + 1).padStart(2, '0');
    const day = String(koreaTime.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    const holidays = year === 2024 ? KOREAN_HOLIDAYS_2024 : year === 2025 ? KOREAN_HOLIDAYS_2025 : [];
    return holidays.includes(dateStr);
  };

  const isTradingHours = (date = new Date()) => {
    const koreaTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const day = koreaTime.getDay();
    const hour = koreaTime.getHours();
    const minutes = koreaTime.getMinutes();
    const isWeekday = day >= 1 && day <= 5;
    const isTradingTime = hour >= 9 && (hour < 15 || (hour === 15 && minutes <= 30));
    return isWeekday && isTradingTime;
  };

  const isMarketOpen = (date = new Date()) => {
    const koreaTime = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const day = koreaTime.getDay();
    const isWeekday = day >= 1 && day <= 5;
    return isWeekday && !isHoliday(date) && isTradingHours(date);
  };

  const computeMarketStatus = () => {
    const open = isMarketOpen();
    setMarketStatus(open ? '주문가능시간' : '정규시장종료');
  };

  useEffect(() => {
    // 초기 1회 + 60초 주기: /api/health 1회 + /api/ai-trade/status 1회 (토큰·rate limit 고려)
    checkTradingService();
    computeMarketStatus();
    const healthInterval = setInterval(checkTradingService, 60000); // 60초 (반복 호출 완화)
    const marketInterval = setInterval(computeMarketStatus, 60000); // 1분
    return () => {
      clearInterval(healthInterval);
      clearInterval(marketInterval);
    };
  }, [checkTradingService]);

  // 헤더에서 탭 변경을 위한 전역 함수 설정
  useEffect(() => {
    window.setTradingTab = (tab) => {
      setActiveTab(tab);
    };
    return () => {
      delete window.setTradingTab;
    };
  }, []);

  return (
    <>
      {showHeader && <TradingHeader onOpenProfile={() => setShowProfile(true)} />}
      {showProfile && (
        <Profile
          user={user}
          onClose={() => setShowProfile(false)}
          onUpdate={async (updatedUser) => {
            console.log('Profile updated, refreshing user data...', {
              id: updatedUser?.id,
              nickname: updatedUser?.nickname,
              name: updatedUser?.name,
              email: updatedUser?.email,
              hasTrading: !!updatedUser?.trading
            });
            
            await fetchUserInfo();
            const latestUser = {
              ...updatedUser,
              name: updatedUser?.name || user?.name,
              nickname: updatedUser?.nickname || user?.nickname
            };
            setUser(latestUser);
            setShowProfile(false);
            const trading = latestUser?.trading || updatedUser?.trading || {};
            const isPaper = trading.is_paper_trading !== false;
            const htsId = isPaper ? (trading.paper_hts_id || trading.hts_id) : (trading.real_hts_id || trading.hts_id);
            if (htsId) {
              console.log('✅ HTS ID saved:', htsId, 'Mode:', isPaper ? 'Paper' : 'Real');
            } else {
              console.error('⚠️ HTS ID not found, please save HTS ID in profile');
              alert(`${isPaper ? '모의투자' : '실전투자'} HTS ID가 설정되지 않았습니다. 프로필에서 HTS ID를 입력해주세요.`);
            }
            if (latestUser?.name && latestUser.name.trim() !== '') {
              console.log('✅ Name saved:', latestUser.name);
            } else {
              console.error('⚠️ Name not found, please save name in profile');
              alert('실명(이름)이 설정되지 않았습니다. 프로필에서 실명을 입력해주세요.');
            }
            if (selectedStock) fetchStockData(selectedStock);
            fetchPortfolio();
            fetchBalance();
            fetchOrderHistory();
          }}
        />
      )}
      <div className="trading-container" style={{ paddingTop: showHeader ? '100px' : '20px' }}>
        {showLogin && (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onClose={() => {
                setShowLogin(false);
            }}
          />
        )}

      {/* 로그인 필요 안내 */}
      {authError && !showLogin && (
        <div className="auth-warning">
          <div className="auth-warning-content">
            <strong>⚠️ 로그인이 필요합니다</strong>
            <p>주식 거래 서비스를 이용하려면 로그인이 필요합니다.</p>
            <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>거래 서비스는 실명(이름)이 필요합니다. 회원가입 시 실명을 입력해 주세요.</p>
          </div>
          <div className="auth-warning-actions">
            <button 
              className="btn-login-primary"
              onClick={() => setShowLogin(true)}
            >
              로그인
            </button>
            <button 
              className="btn-register-primary"
              onClick={() => {
                setShowLogin(true);
                // Login 컴포넌트에서 회원가입 탭으로 전환하도록 처리
                setTimeout(() => {
                  const registerTab = document.querySelector('.login-tabs button:last-child');
                  if (registerTab) registerTab.click();
                }, 100);
              }}
            >
              회원가입
            </button>
          </div>
        </div>
      )}

      {/* 사용자 정보 및 로그아웃 - 헤더 바로 아래에 표시 (소개/기능 페이지에서는 숨김) */}
      {user && (user.nickname || user.name || user.email) && activeTab !== 'intro' && (
        <div 
          className="user-info" 
          style={{ 
            display: 'flex !important', 
            visibility: 'visible !important', 
            opacity: 1,
            position: 'relative',
            zIndex: 10,
            width: '100%',
            maxWidth: '100%',
            marginTop: '0', /* padding-top으로 공간 확보 */
            marginBottom: '20px',
            boxSizing: 'border-box',
            transform: 'translateY(0)',
            overflow: 'visible',
            backgroundColor: '#fff',
            padding: '15px 20px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
          data-testid="user-info-section"
        >
          <div className="user-info-left" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', flex: 1 }}>
            <strong style={{ fontSize: '16px', color: '#2e7d32', fontWeight: 600 }}>
              👤 {user.nickname || user.name || '사용자'}
            </strong>
            {user.name && (
              <span className="user-name" style={{ color: '#555', fontSize: '14px' }}>
                ({user.name})
              </span>
            )}
            {user.email && (
              <span className="user-email" style={{ color: '#666', fontSize: '13px' }}>
                ({user.email})
              </span>
            )}
            {user.services && user.services.includes('trading') && (
              <span 
                className="service-badge" 
                style={{
                  padding: '4px 10px',
                  background: '#4caf50',
                  color: 'white',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                ✓ 주식거래 서비스 활성화
              </span>
            )}
            {user.trading && (
              <span 
                className="trading-mode"
                style={{
                  padding: '4px 10px',
                  background: '#2196f3',
                  color: 'white',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  visibility: 'visible',
                  opacity: 1
                }}
              >
                {user.trading.is_paper_trading !== false ? '📝 모의투자' : '💰 실전투자'}
              </span>
            )}
            {!user.trading && user.services && user.services.includes('trading') && (
              <span 
                className="trading-mode"
                style={{
                  padding: '4px 10px',
                  background: '#ff9800',
                  color: 'white',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  visibility: 'visible',
                  opacity: 1
                }}
              >
                ⚠️ 거래 설정 필요
              </span>
            )}
            {dbConnectionStatus.message && (
              <span 
                className="db-status"
                style={{
                  padding: '4px 10px',
                  background: dbConnectionStatus.status === 'connected' ? '#4caf50' : '#ff9800',
                  color: 'white',
                  borderRadius: '3px',
                  fontSize: '12px',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  display: 'inline-block'
                }}
              >
                {dbConnectionStatus.message}
              </span>
            )}
            <div className="user-info-actions" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <button
                type="button"
                className="btn-profile"
                onClick={() => setShowProfile(true)}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                title="KIS API·모의/실전 설정"
              >
                프로필
              </button>
              <button
                type="button"
                onClick={toggleHeader}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 600,
                  backgroundColor: showHeader ? '#6c757d' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                title={showHeader ? '상단 헤더 숨기기' : '상단 헤더 표시'}
              >
                해더 노출 {showHeader ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 소개/기능 섹션 */}
      {activeTab === 'intro' && (
        <IntroSection />
      )}

      {/* 상태 표시 및 탭 메뉴 (소개 페이지에서는 숨김) */}
      {activeTab !== 'intro' && (
        <>
          <div className="trading-status-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '8px' }}>
            <div className={`service-status ${tradingServiceStatus.status === '실행중' ? 'ok' : tradingServiceStatus.status === '문제' ? 'warn' : tradingServiceStatus.status === 'unknown' ? 'checking' : 'down'}`}>
              {tradingServiceStatus.status === '실행중' ? 'trading-service: 실행중' : tradingServiceStatus.status === '문제' ? 'trading-service: 문제' : tradingServiceStatus.status === 'unknown' ? 'trading-service: 확인중' : 'trading-service: 중단'}
            </div>
            <button
              type="button"
              className={`service-toggle ${serviceEnabled ? 'on' : 'off'}`}
              onClick={toggleTradingService}
              disabled={serviceToggleLoading || user?.email !== 'begite@naver.com'}
              title={user?.email === 'begite@naver.com' ? 'trading-service 토글' : '관리자 전용'}
            >
              {serviceToggleLoading ? '처리 중...' : serviceEnabled ? 'OFF' : 'ON'}
            </button>
            <div className={`market-status ${marketStatus === '주문가능시간' ? 'open' : ''}`}>
              {marketStatus === '주문가능시간' ? '주문가능시간' : '정규시장종료'}
            </div>
          </div>

          <div className="trading-tabs">
        <button 
          className={activeTab === 'intro' ? 'active' : ''}
          onClick={() => setActiveTab('intro')}
        >
          📖 소개
        </button>
        <button 
          className={activeTab === 'trade' ? 'active' : ''}
          onClick={() => setActiveTab('trade')}
        >
          📈 주문
        </button>
        <button 
          className={activeTab === 'portfolio' ? 'active' : ''}
          onClick={() => setActiveTab('portfolio')}
        >
          💼 포트폴리오
        </button>
        <button 
          className={activeTab === 'balance' ? 'active' : ''}
          onClick={() => setActiveTab('balance')}
        >
          💰 잔고
        </button>
        <button 
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          📋 주문내역
        </button>
        <button 
          className={activeTab === 'ai-trade' ? 'active' : ''}
          onClick={() => setActiveTab('ai-trade')}
          data-tab="ai-trade"
        >
          🤖 AI거래설정
        </button>
        <button 
          className={activeTab === 'notification' ? 'active' : ''}
          onClick={() => setActiveTab('notification')}
        >
          🔔 알림설정
        </button>
      </div>
        </>
      )}

      {/* 주문 탭 */}
      {activeTab === 'trade' && (
        <TradeTab
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          searchResults={searchResults}
          setSearchResults={setSearchResults}
          showSearchResults={showSearchResults}
          setShowSearchResults={setShowSearchResults}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          selectedStock={selectedStock}
          setSelectedStock={setSelectedStock}
          stockData={stockData}
          setStockData={setStockData}
          loading={loading}
          setLoading={setLoading}
          refreshingName={refreshingName}
          setRefreshingName={setRefreshingName}
          orderType={orderType}
          setOrderType={setOrderType}
          quantity={quantity}
          setQuantity={setQuantity}
          price={price}
          setPrice={setPrice}
          orderMethod={orderMethod}
          setOrderMethod={setOrderMethod}
          favoriteStocks={favoriteStocksList}
          setFavoriteStocks={setFavoriteStocksList}
          fetchFavoriteStocks={fetchFavoriteStocks}
          showFavoriteStockModal={showFavoriteStockModal}
          setShowFavoriteStockModal={setShowFavoriteStockModal}
          watchlist={watchlist}
          fetchWatchlist={fetchWatchlist}
          popularStocksTab={popularStocksTab}
          setPopularStocksTab={setPopularStocksTab}
          popularStocksDropdownOpen={popularStocksDropdownOpen}
          setPopularStocksDropdownOpen={setPopularStocksDropdownOpen}
          portfolioDropdownOpen={portfolioDropdownOpen}
          setPortfolioDropdownOpen={setPortfolioDropdownOpen}
          popularStocksList={popularStocksList}
          setPopularStocksList={setPopularStocksList}
          aiRecommendedStocksList={aiRecommendedStocksList}
          setAiRecommendedStocksList={setAiRecommendedStocksList}
          watchlistStocksList={watchlistStocksList}
          setWatchlistStocksList={setWatchlistStocksList}
          portfolio={portfolio}
          fetchPortfolio={fetchPortfolio}
          aiAnalysis={aiAnalysis}
          setAiAnalysis={setAiAnalysis}
          buildAnalysisMetadata={buildAnalysisMetadata}
          aiAnalyses={aiAnalyses}
          aiQuestion={aiQuestion}
          setAiQuestion={setAiQuestion}
          aiAnswer={aiAnswer}
          setAiAnswer={setAiAnswer}
          aiQuestionLoading={aiQuestionLoading}
          setAiQuestionLoading={setAiQuestionLoading}
          simpleExplanation={simpleExplanation}
          setSimpleExplanation={setSimpleExplanation}
          explanationLoading={explanationLoading}
          setExplanationLoading={setExplanationLoading}
          itemExplanations={itemExplanations}
          setItemExplanations={setItemExplanations}
          itemExplanationLoading={itemExplanationLoading}
          setItemExplanationLoading={setItemExplanationLoading}
          authenticatedFetch={authenticatedFetch}
          fetchStockData={fetchStockData}
          executeOrder={executeOrder}
          requestAiAnalysis={requestAiAnalysis}
          updateAiAnalysis={updateAiAnalysis}
          fetchAiAnalyses={fetchAiAnalyses}
          aiAnalysisOptions={aiAnalysisOptions}
          setAiAnalysisOptions={setAiAnalysisOptions}
          askAiQuestion={askAiQuestion}
          generateSimpleExplanation={generateSimpleExplanation}
          generateItemExplanation={generateItemExplanation}
          resetAiAnalysis={resetAiAnalysis}
          refreshStockName={refreshStockName}
          authError={authError}
          defaultPopularStocks={popularStocks}
          setSelectedAnalysis={setSelectedAnalysis}
          setShowAnalysisDetail={setShowAnalysisDetail}
          setActiveTab={setActiveTab}
        />
      )}

      {activeTab === 'portfolio' && (
        <PortfolioTab
          aiTradeStatus={aiTradeStatus}
          setShowScheduledOrderModal={setShowScheduledOrderModal}
          setSelectedScheduledOrder={setSelectedScheduledOrder}
          setSelectedStock={setSelectedStock}
          fetchStockData={fetchStockData}
          setActiveTab={setActiveTab}
          fetchPortfolio={fetchPortfolio}
          portfolio={portfolio}
          portfolioLoading={portfolioLoading}
          scheduledOrders={scheduledOrders}
          fetchScheduledOrders={fetchScheduledOrders}
          cancelScheduledOrder={cancelScheduledOrder}
          authError={authError}
          autoTrades={autoTrades}
          setSelectedAutoTrade={setSelectedAutoTrade}
          setShowAutoTradeModal={setShowAutoTradeModal}
          user={user}
          fetchAiTradeStatus={fetchAiTradeStatus}
          fetchAutoTrades={fetchAutoTrades}
          executeOrder={executeOrder}
          setOrderType={setOrderType}
          setQuantity={setQuantity}
          setPrice={setPrice}
          setOrderMethod={setOrderMethod}
        />
      )}

      {activeTab === 'notification' && (
        <NotificationSettingsTab authenticatedFetch={authenticatedFetch} user={user} />
      )}

      {activeTab === 'ai-trade' && (
        <AITradeTab
          user={user}
          aiTradeStatus={aiTradeStatus}
          setShowAiTradeModal={setShowAiTradeModal}
          loading={loading}
          setLoading={setLoading}
          authenticatedFetch={authenticatedFetch}
          fetchAiTradeStatus={fetchAiTradeStatus}
          fetchScheduledOrders={fetchScheduledOrders}
          fetchWatchlist={fetchWatchlist}
          watchlist={watchlist}
          showWatchlist={showWatchlist}
          setShowWatchlist={setShowWatchlist}
          aiAnalyses={aiAnalyses}
          setAiAnalyses={setAiAnalyses}
          selectedAnalysis={selectedAnalysis}
          setSelectedAnalysis={setSelectedAnalysis}
          showAnalysisDetail={showAnalysisDetail}
          setShowAnalysisDetail={setShowAnalysisDetail}
          setSelectedStock={setSelectedStock}
          setActiveTab={setActiveTab}
          fetchStockData={fetchStockData}
          fetchAiAnalyses={fetchAiAnalyses}
        />
      )}

      {activeTab === 'balance' && (
        <BalanceTab
          balance={balance}
          balanceError={balanceError}
          balanceLoading={balanceLoading}
          fetchBalance={fetchBalance}
          performanceVisible={performanceVisible}
          performanceStats={performanceStats}
          performanceLoading={performanceLoading}
          performanceError={performanceError}
          performanceRange={performanceRange}
          onTogglePerformance={handleTogglePerformance}
          onSelectPerformanceRange={handleSelectPerformanceRange}
          onApplyCustomRange={handleApplyCustomPerformanceRange}
          onChangePerformanceRange={setPerformanceRange}
        />
      )}

      {activeTab === 'history' && (
        <HistoryTab
          orderHistory={orderHistory}
          orderHistoryLoading={orderHistoryLoading}
          orderHistoryRateLimited={orderHistoryRateLimited}
          orderHistoryRateLimitUntil={orderHistoryRateLimitUntil}
          fetchOrderHistory={fetchOrderHistory}
          authenticatedFetch={authenticatedFetch}
          user={user}
          marketStatus={marketStatus}
          onUserUpdated={async (forceRefresh, updatedUser) => {
            if (updatedUser) {
              setUser(updatedUser);
            } else {
              await fetchUserInfo(!!forceRefresh);
            }
          }}
          aiTradeConfig={aiTradeConfig}
          fetchAiTradeConfig={fetchAiTradeConfig}
          dateFilter={dateFilter}
          setDateFilter={setDateFilter}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          setCustomStartDate={setCustomStartDate}
          setCustomEndDate={setCustomEndDate}
        />
      )}

      {/* 자동거래 설정 모달 */}
      {showAutoTradeModal && (
        <AutoTradeModal
          autoTrade={selectedAutoTrade}
          authenticatedFetch={authenticatedFetch}
          onClose={() => {
            setShowAutoTradeModal(false);
            setSelectedAutoTrade(null);
          }}
          onSave={async () => {
            // 자동거래 설정 다시 불러오기
            await fetchAutoTrades();
            // 포트폴리오도 새로고침하여 자동거래 설정 표시 업데이트
            await fetchPortfolio();
            setShowAutoTradeModal(false);
            setSelectedAutoTrade(null);
          }}
        />
      )}

      {/* 예약 주문 모달 */}
      {showScheduledOrderModal && (
        <ScheduledOrderModal
          portfolioItem={selectedScheduledOrder}
          authenticatedFetch={authenticatedFetch}
          onClose={() => {
            setShowScheduledOrderModal(false);
            setSelectedScheduledOrder(null);
          }}
          onSave={async () => {
            await fetchScheduledOrders();
            setShowScheduledOrderModal(false);
            setSelectedScheduledOrder(null);
          }}
        />
      )}

      {/* AI 거래 설정 모달 */}
      {showAiTradeModal && (
        <AITradeConfigModal
          config={aiTradeConfig}
          authenticatedFetch={authenticatedFetch}
          user={user}
          onClose={() => {
            setShowAiTradeModal(false);
          }}
          onSave={async () => {
            await fetchAiTradeStatus();
            // AI 거래 설정 다시 강제 조회 (업데이트 반영)
            await fetchAiTradeConfig(true);
            setShowAiTradeModal(false);
          }}
          onOpen={() => fetchAiTradeConfig(false)}
        />
      )}
      </div>
      {/* Footer - trading-service 전용 */}
      <Footer />
    </>
  );
};

export default Trading;
